import {
	ArrowRight,
	CheckCircle2,
	Clock,
	Eye,
	FileText,
	Plus,
	TrendingUp,
	Users,
	XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
	Notification,
	StatCard,
	useNotification,
} from "../../components/FormComponents";
import StatusBadge from "../../components/StatusBadge";
import { useDashboardMetrics } from "../../hooks/useDashboardMetrics";
import { formatCurrency, formatDate } from "../../utils/format";

export default function DashboardHome() {
	const navigate = useNavigate();
	const { notification, showNotification } = useNotification();
	const {
		counts,
		recentProposals,
		monthlyProposals,
		recentActivity,
		maxMonthly,
		loading,
	} = useDashboardMetrics(showNotification);

	if (loading) {
		return (
			<div className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
					{[1, 2, 3, 4].map((i) => (
						<div
							key={`skeleton-card-${i}`}
							className="bg-slate-900/50 border border-white/5 rounded-xl p-6 animate-pulse"
						>
							<div className="h-4 bg-slate-700/50 rounded w-24 mb-3" />
							<div className="h-8 bg-slate-700/50 rounded w-16" />
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<div>
				<h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
				<p className="text-slate-400">Visão geral do sistema de propostas</p>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
				<StatCard
					icon={FileText}
					label="Total Propostas"
					value={counts.proposals || 0}
					color="bg-cyan-600/20"
					textColor="text-cyan-400"
					subtitle={`${counts.pending || 0} pendentes`}
				/>
				<StatCard
					icon={CheckCircle2}
					label="Aceitas"
					value={counts.accepted || 0}
					color="bg-green-600/20"
					textColor="text-green-400"
					subtitle={formatCurrency(counts.accepted_value)}
				/>
				<StatCard
					icon={XCircle}
					label="Rejeitadas"
					value={counts.rejected || 0}
					color="bg-red-600/20"
					textColor="text-red-400"
				/>
				<StatCard
					icon={Users}
					label="Clientes"
					value={counts.clients || 0}
					color="bg-purple-600/20"
					textColor="text-purple-400"
					subtitle={`${counts.companies || 0} empresas`}
				/>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Monthly Chart */}
				<div className="lg:col-span-2 bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
					<div className="flex items-center justify-between mb-6">
						<h2 className="text-lg font-semibold text-white">
							Propostas por Mês
						</h2>
						<TrendingUp className="w-5 h-5 text-cyan-400" />
					</div>

					{monthlyProposals.length > 0 ? (
						<div className="flex items-end gap-3 h-48">
							{monthlyProposals.map((m, idx) => {
								const count = parseInt(m.count, 10) || 0;
								const height = Math.max((count / maxMonthly) * 100, 4);
								const monthNames = [
									"Jan",
									"Fev",
									"Mar",
									"Abr",
									"Mai",
									"Jun",
									"Jul",
									"Ago",
									"Set",
									"Out",
									"Nov",
									"Dez",
								];
								const monthLabel =
									monthNames[parseInt(m.month, 10) - 1] || m.month;
								return (
									<div
										key={`chart-bar-${m.month}-${idx}`}
										className="flex-1 flex flex-col items-center gap-2"
									>
										<span className="text-xs text-cyan-400 font-bold">
											{count}
										</span>
										<div
											className="w-full bg-gradient-to-t from-cyan-600/60 to-cyan-400/40 rounded-t-lg border border-cyan-500/20 transition-all hover:from-cyan-600/80 hover:to-cyan-400/60"
											style={{ height: `${height}%` }}
										/>
										<span className="text-xs text-slate-500">{monthLabel}</span>
									</div>
								);
							})}
						</div>
					) : (
						<div className="flex items-center justify-center h-48 text-slate-500 text-sm">
							Nenhum dado disponível
						</div>
					)}
				</div>

				{/* Recent Activity */}
				<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
					<h2 className="text-lg font-semibold text-white mb-4">
						Atividade Recente
					</h2>
					{recentActivity.length > 0 ? (
						<div className="space-y-3">
							{recentActivity.map((item, idx) => (
								<div
									key={item.id || `activity-${idx}`}
									className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50"
								>
									<div
										className={`w-2 h-2 rounded-full flex-shrink-0 ${
											item.response_type === "accepted"
												? "bg-green-500"
												: "bg-red-500"
										}`}
									/>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-white truncate">
											{item.proposal_number || `#${item.proposal_id}`}
										</p>
										<p className="text-xs text-slate-500">
											{item.response_type === "accepted"
												? "Aceita"
												: "Rejeitada"}{" "}
											por {item.approver_name || "N/A"}
										</p>
									</div>
									<span className="text-xs text-slate-500">
										{formatDate(item.responded_at)}
									</span>
								</div>
							))}
						</div>
					) : (
						<div className="flex items-center justify-center py-8 text-slate-500 text-sm">
							Nenhuma atividade recente
						</div>
					)}
				</div>
			</div>

			{/* Recent Proposals */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold text-white">
						Propostas Recentes
					</h2>
					<button
						type="button"
						onClick={() => navigate("/admin/proposals")}
						className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition"
					>
						Ver todas <ArrowRight className="w-4 h-4" />
					</button>
				</div>

				{recentProposals.length > 0 ? (
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead className="bg-slate-800/50 border-b border-slate-700">
								<tr>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Proposta
									</th>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Cliente
									</th>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Empresa
									</th>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Status
									</th>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Valor
									</th>
									<th className="px-4 py-3 text-left font-medium text-slate-300">
										Data
									</th>
									<th
										className="px-4 py-3 text-left font-medium text-slate-300"
										aria-label="Ações"
									/>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-700">
								{recentProposals.map((p, idx) => (
									<tr
										key={p.id || `proposal-${idx}`}
										className="hover:bg-slate-800/30 transition"
									>
										<td className="px-4 py-3">
											<span className="font-mono text-cyan-300 font-bold">
												{p.proposal_number}
											</span>
										</td>
										<td className="px-4 py-3 text-slate-300">
											{p.client_name}
										</td>
										<td className="px-4 py-3 text-slate-400">
											{p.company_name}
										</td>
										<td className="px-4 py-3">
											<StatusBadge status={p.status} />
										</td>
										<td className="px-4 py-3 text-green-400 font-bold">
											{formatCurrency(p.total_value)}
										</td>
										<td className="px-4 py-3 text-slate-500 text-xs">
											{formatDate(p.created_at)}
										</td>
										<td className="px-4 py-3">
											<button
												type="button"
												onClick={() => navigate(`/admin/proposals/${p.id}`)}
												className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-cyan-600/10 rounded transition"
												aria-label={`Ver proposta ${p.proposal_number}`}
											>
												<Eye className="w-4 h-4" />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className="text-center py-8 text-slate-500 text-sm">
						Nenhuma proposta criada ainda
					</div>
				)}
			</div>

			{/* Quick Actions */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<button
					type="button"
					onClick={() => navigate("/admin/proposals/new")}
					className="flex items-center gap-3 px-6 py-4 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 rounded-xl text-cyan-300 font-medium transition group"
				>
					<Plus className="w-5 h-5 group-hover:scale-110 transition-transform" />
					Criar Proposta
				</button>
				<button
					type="button"
					onClick={() => navigate("/admin/clients")}
					className="flex items-center gap-3 px-6 py-4 bg-purple-600/10 hover:bg-purple-600/20 border border-purple-500/20 rounded-xl text-purple-300 font-medium transition group"
				>
					<Users className="w-5 h-5 group-hover:scale-110 transition-transform" />
					Gerenciar Clientes
				</button>
				<button
					type="button"
					onClick={() => navigate("/admin/proposals")}
					className="flex items-center gap-3 px-6 py-4 bg-amber-600/10 hover:bg-amber-600/20 border border-amber-500/20 rounded-xl text-amber-300 font-medium transition group"
				>
					<Clock className="w-5 h-5 group-hover:scale-110 transition-transform" />
					Ver Pendentes ({counts.pending || 0})
				</button>
			</div>
		</div>
	);
}
